import { zodResolver } from '@hookform/resolvers/zod';
import { type SignInOutput, useConfirmSignIn, useSignIn } from 'korint-sdk/react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { z } from 'zod';

import { Alert } from '../../ui/alert';
import { Button } from '../../ui/button';
import { Form, FormControl, FormField, FormItem, FormMessage } from '../../forms/form';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '../../forms/input-otp';
import { Text } from '@folio/components/ui/text';

export function SmsChallengeForm({
  email,
  password,
  destination,
  onSuccess,
}: {
  email: string;
  password: string;
  destination?: string;
  onSuccess: () => void;
}) {
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);
  const [resendError, setResendError] = useState<string | null>(null);
  const [cooldown, setCooldown] = useState(0);
  const [isResending, setIsResending] = useState(false);

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setInterval(() => setCooldown(c => c - 1), 1000);
    return () => clearInterval(timer);
  }, [cooldown]);

  const { confirmSignIn, isLoading } = useConfirmSignIn({
    onSuccess: (result: SignInOutput | undefined) => {
      if (result?.isSignedIn) {
        onSuccess();
      } else {
        setError(t('authSmsChallenge.errorGeneric'));
      }
    },
    onError: err => setError(mapSmsError(err, t)),
  });

  // Bare instances (no onSuccess/onError) — handleResend awaits and branches on
  // the result itself, so the code-submission form's callbacks above don't
  // also fire (and misreport a re-selection step as an error).
  const { signIn } = useSignIn();
  const { confirmSignIn: reselectSms } = useConfirmSignIn();

  const handleResend = async () => {
    if (cooldown > 0 || isResending) return;
    setResendError(null);
    setIsResending(true);
    try {
      // No dedicated resend API; re-invoking signIn restarts the challenge. On
      // a pool with a single preferred method this re-sends the code
      // directly. On a pool offering a choice of methods (reached via
      // MfaSelectionForm), signIn instead comes back to the selection step
      // with no code sent yet — re-select SMS to actually trigger delivery.
      let result = await signIn({ username: email, password });
      if (
        !result?.isSignedIn &&
        result?.nextStep.signInStep === 'CONTINUE_SIGN_IN_WITH_MFA_SELECTION'
      ) {
        result = await reselectSms({ challengeResponse: 'SMS' });
      }
      if (result?.isSignedIn) {
        onSuccess();
        return;
      }
      if (result?.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_SMS_CODE') {
        setCooldown(60);
      } else {
        setResendError(t('authSmsChallenge.errorGeneric'));
      }
    } catch (err) {
      setResendError(err instanceof Error ? err.message : t('authSmsChallenge.errorGeneric'));
    } finally {
      setIsResending(false);
    }
  };

  const form = useForm<{ code: string }>({
    defaultValues: { code: '' },
    resolver: zodResolver(z.object({ code: z.string().length(6) })),
  });

  const handleSubmit = form.handleSubmit(async ({ code }) => {
    setError(null);
    await confirmSignIn({ challengeResponse: code });
  });

  return (
    <Form {...form}>
      <Text size="sm" tone="muted" className="mb-4">
        {destination
          ? t('authSmsChallenge.subtitleDestination', { destination })
          : t('authSmsChallenge.subtitle', { email })}
      </Text>
      {resendError && (
        <Alert className="mb-4" variant="destructive">
          {resendError}
        </Alert>
      )}
      {error && (
        <Alert className="mb-4" variant="destructive">
          {error}
        </Alert>
      )}
      <form onSubmit={handleSubmit} className="flex flex-col items-center gap-4">
        <FormField
          control={form.control}
          name="code"
          render={({ field }) => (
            <FormItem className="items-center">
              <FormControl>
                <InputOTP maxLength={6} autoFocus {...field}>
                  <InputOTPGroup>
                    {Array.from({ length: 6 }, (_, index) => (
                      <InputOTPSlot key={`sms-slot-${index}`} index={index} />
                    ))}
                  </InputOTPGroup>
                </InputOTP>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <Button
          className="w-full"
          type="submit"
          loading={isLoading}
          disabled={form.watch('code').length < 6}
        >
          {t('authSmsChallenge.submit')}
        </Button>
        <Button
          className="w-full"
          type="button"
          variant="outline"
          onClick={handleResend}
          disabled={cooldown > 0 || isResending}
          loading={isResending}
        >
          {cooldown > 0
            ? t('authSmsChallenge.resendCooldown', { seconds: cooldown })
            : t('authSmsChallenge.resend')}
        </Button>
      </form>
    </Form>
  );
}

function mapSmsError(err: unknown, t: TFunction): string {
  if (!(err instanceof Error)) {
    return t('authSmsChallenge.errorGeneric');
  }
  const name = (err as Error & { name?: string }).name ?? '';
  if (name === 'CodeMismatchException') return t('authTotpChallenge.errorInvalidCode');
  if (name === 'ExpiredCodeException') return t('authTotpChallenge.errorCodeExpired');
  if (name === 'LimitExceededException' || name === 'TooManyRequestsException') {
    return t('authTotpChallenge.errorRateLimited');
  }
  if (name === 'CodeDeliveryFailureException') return t('authSmsChallenge.errorDeliveryFailed');
  if (name === 'InvalidParameterException') return t('authSmsChallenge.errorInvalidPhone');
  return err.message || t('authSmsChallenge.errorGeneric');
}
